import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import styles from '../../views/ProductDetails/ProductDetails.module.scss';
import ultraStyles from './UltraPDP.module.scss';
import HideChatWidget from '../ZendeskChat/HideChatWidget';
import Footer from '../Footer/Footer';
import NavBar from '../AppBar/NavBar';
import PlusMembershipValueProps from '../../views/ProductDetails/Series3CollarDetails/components/PlusMembershipValueProps';
import FAQ from '../../views/ProductDetails/Series3CollarDetails/components/FAQ';
import CollarAddToBagButton from '../../views/ProductDetails/Series3CollarDetails/components/CollarAddToBagButton';
import * as types from '../../types';
import { cartActions } from '../../reducers/storeCart';
import useSubscriptionProducts from '../../hooks/useSubscriptionProducts';
import useHardwareFirstAddGuard from '../../hooks/useHardwareFirstAddGuard';
import { useLocale } from '../../contexts/LocaleContext';
import { CustomerIO } from '../../lib/analytics/customerIO';
import * as events from '../../lib/analytics/events';

import { ProductTitle, ProductTitleDescription, ProductPairingBadge } from './ProductTitle/ProductTitle';
import UltraMembershipOptions from './UltraMembershipOptions/UltraMembershipOptions';
import UltraHardware from './UltraHardware/UltraHardware';
import UltraExistingMember from './UltraExistingMember/UltraExistingMember';
import UltraProductSummary from './UltraProductSummary/UltraProductSummary';
import UltraAppCarousel from './UltraAppCarousel/UltraAppCarousel';
import UltraAllFeatures from './UltraAllFeatures/UltraAllFeatures';
import UltraDesktopImageGallery from './UltraDesktopImageGallery/UltraDesktopImageGallery';
import { BuyingFor } from './WhoAreYouShoppingFor/WhoAreYouShoppingFor';
import UltraMemberOptions from './UltraMemberOptions/UltraMemberOptions';
import useUltraExperienceGuard from './useUltraExperienceGuard';
import useUltraEligiblePets from '../../hooks/useUltraEligiblePets';
import { usePdpImagesContext } from '../../contexts/PdpImagesContext';

export default function UltraPDP() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const products = useSelector((state: types.AppState) => state.config.products);
  const { currency } = useLocale();

  // Get pre-loaded dynamic images from context
  const { getMedia } = usePdpImagesContext();
  const dynamicImages = getMedia(types.ultraCollarId);

  const product = products.find((p) => p.id === types.ultraCollarId) as
    | (types.IUltraProduct & { sku: string })
    | undefined;

  const allUltraSubscriptions = useSubscriptionProducts(types.ultraCollarId);
  const subscriptions = allUltraSubscriptions.filter(
    (p) => !p.thirdParty && p.renewalMonths !== 6 && p.renewalMonths !== 24,
  );

  const initialSubscription = subscriptions.find((s) => s.renewalMonths === 12);
  const [selectedSubscription, setSelectedSubscription] = useState<types.ISubscriptionProduct | undefined>(
    initialSubscription,
  );

  const { pets: eligiblePets, loading: eligiblePetsLoading } = useUltraEligiblePets();
  // Show the member view whenever the shopper is signed in (the app's canonical login signal).
  const loggedIn = useSelector((state: types.AppState) => !!state.session);
  const showPicker = loggedIn;
  // Whether the member has a pet on an active S3/S3+/Mini membership the Ultra can be added to. If
  // not, the "device only" (existing membership) option is hidden and we default to the new flow.
  const hasEligibleMembership = eligiblePets.length > 0;
  const [buyingFor, setBuyingFor] = useState<BuyingFor | null>(null);

  useEffect(() => {
    if (showPicker) {
      events.ultraPDPShoppingForPickerShown({ eligiblePetCount: eligiblePets.length });
    }
  }, [showPicker, eligiblePets.length]);

  // Default the member selection once their pets have loaded: use the existing-membership ($299) path
  // when they have a qualifying membership, otherwise force the new-membership path (no device-only
  // option to fall back on). Deferring until loaded avoids preselecting "new" before the query resolves.
  useEffect(() => {
    if (!showPicker || eligiblePetsLoading) return;
    if (!hasEligibleMembership) {
      if (buyingFor !== 'new') setBuyingFor('new');
    } else if (buyingFor === null) {
      setBuyingFor('existing');
    }
  }, [showPicker, eligiblePetsLoading, hasEligibleMembership, buyingFor]);

  const onSelectBuyingFor = useCallback(
    (choice: BuyingFor) => {
      setBuyingFor(choice);
      events.ultraPDPShoppingForSelected({ choice, eligiblePetCount: eligiblePets.length });
    },
    [eligiblePets.length],
  );

  useEffect(() => {
    if (!selectedSubscription && subscriptions.length > 0) {
      const oneYear = subscriptions.find((s) => s.renewalMonths === 12);
      setSelectedSubscription(oneYear ?? subscriptions[0]);
    }
  }, [subscriptions, selectedSubscription]);

  const productSku = product?.sku ?? 'U1-MU';

  const isUltraSupportedInCountry = useUltraExperienceGuard();
  const { atCap: hardwareFirstAtCap, routeToBag: hardwareFirstRouteToBag } = useHardwareFirstAddGuard();

  const addToBag = useCallback(() => {
    if (showPicker && buyingFor === 'existing') {
      // Existing member: add just the Ultra module (covered by their current membership) — no modal.
      // At the hardware-first unit cap, skip the add the server would reject and let the bag's
      // bulk-sales notice explain.
      if (!hardwareFirstAtCap) {
        dispatch(cartActions.addSingleLineCartItem({ sku: productSku, currency }));
        CustomerIO.trackAddedItemToCart(types.ultraCollarId);
      }
    } else if (selectedSubscription) {
      // New membership: bundle the Ultra hardware with the selected membership term.
      dispatch(
        cartActions.addBundleCartItem({
          sku: productSku,
          subscriptionSku: selectedSubscription.sku,
          addonSkus: [],
          currency,
          isGift: false,
        }),
      );
      CustomerIO.trackAddedItemToCart(types.ultraCollarId);
    } else {
      return false;
    }

    hardwareFirstRouteToBag();
    return true;
  }, [
    showPicker,
    buyingFor,
    dispatch,
    productSku,
    selectedSubscription,
    currency,
    hardwareFirstAtCap,
    hardwareFirstRouteToBag,
  ]);

  if (!isUltraSupportedInCountry || !product) {
    return null;
  }

  const isExistingDogPath = showPicker && buyingFor === 'existing';
  const isNewDogPath = !showPicker || buyingFor === 'new';
  const productPriceInCents = isExistingDogPath ? product.pricingInCents.USD : product.standalonePricingInCents.USD;

  const canAddToBag = isExistingDogPath || (isNewDogPath && !!selectedSubscription);

  return (
    <>
      <div
        className={classNames(styles.productContainer, styles.ecomFlowRevamp, 'ecom-flow-revamp', 'ultra-pdp', {
          'ecom-flow-revamp-light-v2': true,
        })}
      >
        <NavBar />
        <div className={classNames('image-gallery-container', styles.galleryContainer)}>
          <UltraDesktopImageGallery dynamicImages={dynamicImages} />
        </div>
        <div className={classNames(styles.productDetails, 'product-details')}>
          <div className={styles.productTitleContainer}>
            <ProductTitle />
            <ProductTitleDescription />
            <ProductPairingBadge />
            {showPicker ? (
              <UltraMemberOptions
                buyingFor={buyingFor}
                onSelectBuyingFor={onSelectBuyingFor}
                showExistingOption={hasEligibleMembership}
                pets={eligiblePets}
                membersPriceInCents={product.pricingInCents.USD}
                newDevicePriceInCents={product.standalonePricingInCents.USD}
                membershipPriceInCents={selectedSubscription?.pricingInCents.USD}
              />
            ) : (
              <>
                <UltraHardware priceInCents={productPriceInCents} />
                <UltraMembershipOptions
                  subscriptions={subscriptions}
                  selectedSubscription={selectedSubscription}
                  onSelectSubscription={setSelectedSubscription}
                />
                <UltraExistingMember />
              </>
            )}
          </div>
          <PlusMembershipValueProps hideMultiDogDiscount />
          <CollarAddToBagButton
            ctaText={canAddToBag ? t('add_to_cart') : t('ultra_pick_your_membership')}
            descriptionText={null}
            isVisibleOverride={canAddToBag}
            onAddToBag={addToBag}
          />
        </div>
      </div>
      <UltraProductSummary />
      <UltraAppCarousel />
      <UltraAllFeatures />
      <FAQ collarId={types.ultraCollarId} />
      <div className={ultraStyles.footerContainer}>
        <Footer />
      </div>
      <HideChatWidget />
    </>
  );
}
